import {
  Box, Typography, Stack, Divider, Link, Accordion, AccordionSummary,
  AccordionDetails, Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LaunchIcon from "@mui/icons-material/Launch";
import { RichTextView } from "./RichText";
import { GUIDELINE_STATUS_COLOR } from "../constants";

const fmtDate = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString();
};

/**
 * Read-only render of a full guideline: Title, then Author / Reviewer / Last
 * review date in small print, the Introduction, expandable Sections, and a
 * References list (clickable when a URL is given). Shared by the User view and
 * the Admin read-only view.
 */
export default function GuidelineDocument({ guideline, showStatus = false }) {
  if (!guideline) return null;
  const sections = guideline.sections || [];
  const references = guideline.references || [];
  const meta = [
    guideline.author_name && `Author: ${guideline.author_name}`,
    guideline.reviewer_name && `Reviewer: ${guideline.reviewer_name}`,
    guideline.last_review_date && `Last reviewed: ${fmtDate(guideline.last_review_date)}`,
    guideline.version && `Version ${guideline.version}`,
  ].filter(Boolean);

  return (
    <Box>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Typography variant="h5">{guideline.title}</Typography>
        {showStatus && guideline.status && (
          <Chip size="small" label={guideline.status}
                color={GUIDELINE_STATUS_COLOR[guideline.status] || "default"} />
        )}
      </Stack>
      {meta.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {meta.join("  ·  ")}
        </Typography>
      )}

      {guideline.introduction && guideline.introduction.trim() && (
        <Box sx={{ mt: 2 }}>
          <RichTextView value={guideline.introduction} />
        </Box>
      )}

      {sections.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {sections.map((s, i) => (
            <Accordion key={i} defaultExpanded disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>{s.title || `Section ${i + 1}`}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <RichTextView value={s.content} />
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {references.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Divider textAlign="left" sx={{ mb: 1 }}>References</Divider>
          <Stack component="ul" spacing={0.5} sx={{ pl: 3, m: 0 }}>
            {references.map((r, i) => (
              <li key={i}>
                {r.url ? (
                  <Link href={r.url} target="_blank" rel="noopener noreferrer"
                        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    {r.title || r.url}<LaunchIcon sx={{ fontSize: 14 }} />
                  </Link>
                ) : (
                  <Typography component="span">{r.title || "—"}</Typography>
                )}
              </li>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
